import { ChangeDetectorRef, Component, EventEmitter, Input, OnInit, Output, ViewChild } from '@angular/core';
import { CrmForm } from '../crm-form';
import { CompanyBasic, User } from '../../../models';
import { debounceTime, distinctUntilChanged, switchMap } from 'rxjs/operators';
import { Subject } from 'rxjs';
import { CrmService } from '../../../../core/data-services/crm.service';
import { StructCapCRMItem } from '../../../models/crm/struct-cap/StructCapCRMItem.model';
import { EventsHubService } from '../../../../core/events-hub.service';
import { StructCapCRMDealLabels, StructCapDealType } from '../../../enums/struct-cap-crm-deal-type.enum';
import { STRUCT_CAP_BLOCKING_STATUSES, StructCapCRMStatus, StructCapCRMStatusLabels } from '../../../enums/struct-cap-crm-status';
import { CompanyStage, CompanyStageLabels } from '../../../enums/company-stage.enum';
import { HealthcareSubSector, HealthcareSubSectorLabels } from '../../../enums/healthcare-sub-sector.enum';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { StructCapCrmDealSourceType, StructCapCrmDealSourceTypeLabels } from '../../../enums/struct-cap-crm-deal-source-type.enum';
import { AuthenticationService, DateTimeService, RmsUtilsService } from '../../../../core/services';
import { Domain } from '../../../models/domain.model';
import { CRMAlarm } from '../../../models/crm/CRMAlarm.model';
import { CRMCompanyStorm } from '../../../models/crm/CRMCompanyStorm';
import { CompanyService } from '../../../../core/data-services/company';
import { NgForm } from '@angular/forms';
import { StructCapCrmStatusService } from 'src/app/core/data-services/struct-cap-crm-status.service';
import { CrmAlarmUtilsService } from 'src/app/core/data-services/crm-alarm-utils.service';
import { CrmIntegrationService } from '../../../../core/services/crm-integration.service';
import { CrmUtilsService } from '../../../../core/data-services/crm-utils.service';
import { StructCapCrmService } from '../../../../core/data-services/struct-cap-crm.service';
import { StructCapCrmStatusChangeModalComponent } from '../../struct-cap-crm-status-change-modal/struct-cap-crm-status-change-modal.component';
import { DateTime } from 'luxon';
import { STRUCT_CAP_PRIORITY_TIERS, StructCapPriorityTier } from '../../../constants/struct-cap-priority-tiers';

/**
 * What the hint under the Priority field says: nothing entered, outside 1–7, exactly on a tier,
 * or between two tiers (the field steps by .1). `nearestTier` is the tier highlighted in the full list.
 */
export interface StructCapPriorityHint {
  state: 'empty' | 'outOfRange' | 'exact' | 'between';
  value?: number;
  tier?: StructCapPriorityTier;
  lower?: StructCapPriorityTier;
  upper?: StructCapPriorityTier;
  nearestTier?: number;
}

@Component({
    selector: 'rms-crm-struct-cap-form',
    templateUrl: './crm-struct-cap-form.component.html',
    styleUrls: ['./crm-struct-cap-form.component.scss'],
    standalone: false
})
export class CrmStructCapFormComponent implements OnInit, CrmForm {

  @Input() editMode: boolean;
  @Input() inputCrmItem: StructCapCRMItem;
  @Input() inputCompanyId: number;
  @Input() companyList: Array<CompanyBasic> = new Array<CompanyBasic>();

  @Output() formSubmitted = new EventEmitter<any>();

  @ViewChild('addCrmForm', { static: true, read: NgForm }) form: NgForm;

  public item: StructCapCRMItem = new StructCapCRMItem();
  public silenceUntilDate: DateTime;
  public companyTypeahead = new Subject<string>();
  public companySearching = false;
  public companyError = '';
  public moreDetails = false;
  public closingDate: DateTime;
  public ndaDate: DateTime;

  public healthcareSubsectorSelectOptions = [];
  public statusSelectOptions = [];
  public dealTypeSelectOptions = [];
  public dealSourceTypeSelectOptions = [];
  public companyStageSelectOptions = [];
  public readonly DealTypeLabels = StructCapCRMDealLabels;
  public readonly DealSourceTypeLabels = StructCapCrmDealSourceTypeLabels;
  public readonly priorityTiers = STRUCT_CAP_PRIORITY_TIERS;
  public priorityHint: StructCapPriorityHint = { state: 'empty' };
  public showAllPriorityTiers = false;

  private _origItem: StructCapCRMItem;

  constructor(
    private crmService: CrmService,
    private structCapCrmService: StructCapCrmService,
    private cd: ChangeDetectorRef,
    private eventsHubService: EventsHubService,
    private rmsUtils: RmsUtilsService,
    private companyService: CompanyService,
    private authenticationService: AuthenticationService,
    private structCapCrmStatusService: StructCapCrmStatusService,
    private crmAlarmUtilService: CrmAlarmUtilsService,
    private crmIntegrationService: CrmIntegrationService,
    private modalService: NgbModal,
    private crmUtilsService: CrmUtilsService
  ) { }

  ngOnInit() {
    this.companyAutocomplete();
    this.initializeItem();
    this._origItem = RmsUtilsService.deepClone(this.item);
    this.setIsClean();
    this.setSelectOptions();
    this.updatePriorityHint(this.item.priority);
  }

  public submit() {
    if (this.cannotSubmit()) {
      return;
    }

    // If the item is being moved to one of the blocking statuses, require the user post a note explaining why before it saves
    if (this.item.id && STRUCT_CAP_BLOCKING_STATUSES.includes(this.item.status) &&
      (!this.inputCrmItem || (this.inputCrmItem.status !== this.item.status))) {
      const modalRef = this.modalService.open(StructCapCrmStatusChangeModalComponent, { centered: true });
      modalRef.componentInstance.newStatus = this.item.status;
      modalRef.result.then((result) => {
        if (result) {
          this.crmUtilsService.saveNewNote(this.item, result.noteContent, this.structCapCrmService, { notify: result.shoudNotify });
          const item: any = this.getCrmData();
          item.statusReason = result.statusReason || 'n/a';
          this.formSubmitted.next(item);
        }
      });
    } else {
      const item = this.getCrmData();
      this.formSubmitted.next(item);
    }
  }

  public cannotSubmit(): boolean {
    return this.form.invalid || this.hasInValidSilenceDate();
  }

  /**
   * Recomputes the priority hint from the entered value. Called from the input's ngModelChange rather
   * than a getter so the template binds a stable object between change-detection passes.
   */
  public updatePriorityHint(priority: number): void {
    const tierFor = (tierValue: number) => this.priorityTiers.find(t => t.value === tierValue);
    if (priority === null || priority === undefined || Number.isNaN(+priority)) {
      this.priorityHint = { state: 'empty' };
      return;
    }
    const value = +priority;
    const first = this.priorityTiers[0].value;
    const last = this.priorityTiers.at(-1).value;
    if (value < first || value > last) {
      this.priorityHint = { state: 'outOfRange', value };
      return;
    }
    const nearestTier = Math.round(value);
    const tier = tierFor(value);
    this.priorityHint = tier ?
      { state: 'exact', value, tier, nearestTier } :
      { state: 'between', value, lower: tierFor(Math.floor(value)), upper: tierFor(Math.ceil(value)), nearestTier };
  }

  public addCompany() {
    this.eventsHubService.openAddCompanyModal(false);
  }

  public showSilenceUntil(): boolean {
    return this.structCapCrmStatusService.isSilenceStatus(this.item.status);
  }

  public silenceUntilIsBeforeToday(event: DateTime): boolean {
    return event < DateTime.now();
  }

  public setIsClean(): void {
    this.crmIntegrationService.isClean = () => this.isClean(this.item, this._origItem);
  }

  private isClean(item: StructCapCRMItem, origItem: StructCapCRMItem): boolean {
    return item.name === origItem.name &&
      (item.companies[0].company && item.companies[0].company.id) ===
      (origItem.companies[0].company && origItem.companies[0].company.id) &&
      item.status === origItem.status &&
      (item.leader && item.leader.id) === (origItem.leader && origItem.leader.id) &&
      item.participants.every(p => origItem.participants.find(op => op.user.id === p.user.id)) &&
      item.capIqId === origItem.capIqId &&
      item.totalDealSize === origItem.totalDealSize &&
      item.raDealSize === origItem.raDealSize &&
      item.priority === origItem.priority &&
      item.dealSourceContact === origItem.dealSourceContact &&
      item.companyStage === origItem.companyStage &&
      item.healthcareSubSector === origItem.healthcareSubSector &&
      item.diseaseFocus === origItem.diseaseFocus &&
      item.location === origItem.location &&
      item.expectedClosingDate === origItem.expectedClosingDate &&
      item.revenueModel === origItem.revenueModel &&
      item.downsideSupport === origItem.downsideSupport &&
      item.ltmRevenue === origItem.ltmRevenue &&
      item.ltmEbitda === origItem.ltmEbitda &&
      item.expectedIrr === origItem.expectedIrr &&
      item.expectedMoic === origItem.expectedMoic &&
      item.lastRoundValuation === origItem.lastRoundValuation &&
      item.currentRoundValuation === origItem.currentRoundValuation &&
      item.ndaSigned === origItem.ndaSigned &&
      item.ndaExpiration === origItem.ndaExpiration &&
      item.dealTypes.every(dt => origItem.dealTypes.find(odt => odt === dt)) &&
      item.dealSourceType === origItem.dealSourceType &&
      item.dealSourceTypeFreeText === origItem.dealSourceTypeFreeText &&
      item.sourceUsers.every(su => origItem.sourceUsers.find(osu => osu.id === su.id)) &&
      item.silenceUntil === origItem.silenceUntil;
  }

  private hasInValidSilenceDate(): boolean {
    return this.item.status === StructCapCRMStatus.PASSED_FLAGGED_FOR_FOLLOW_UP &&
        (this.silenceUntilDate == null ||
        this.silenceUntilIsBeforeToday(this.silenceUntilDate));
  }

  private initializeItem() {
    if (this.inputCrmItem) {
      this.item.id = this.inputCrmItem.id;
      this.item.name = this.inputCrmItem.name;
      this.item.status = this.inputCrmItem.status;
      this.item.description = this.inputCrmItem.description;
      this.item.domains = [...this.inputCrmItem.domains];
      this.item.leader = this.inputCrmItem.leader;
      this.item.triageOwner = this.inputCrmItem.triageOwner;
      this.item.participants = [...this.inputCrmItem.participants];
      this.item.indicated = this.inputCrmItem.indicated;
      this.item.received = this.inputCrmItem.received;
      this.item.externalIds = [...this.inputCrmItem.externalIds];
      this.item.companies = this.inputCrmItem.companies && this.inputCrmItem.companies.length > 0 ? [{
        ...this.inputCrmItem.companies[0],
        company: {...this.inputCrmItem.companies[0].company}
      }] : [{company: null, storm: null, etaki: null}];
      this.item.alarms = [...this.inputCrmItem.alarms];

      const silenceAlarm = this.crmAlarmUtilService.findSilenceAlarm(this.inputCrmItem);
      this.silenceUntilDate = silenceAlarm ? DateTimeService.parseDate(silenceAlarm.trigger.value) : null;
      this.item.capIqId = this.inputCrmItem.capIqId;
      this.item.totalDealSize = this.inputCrmItem.totalDealSize;
      this.item.raDealSize = this.inputCrmItem.raDealSize;
      this.item.priority = this.inputCrmItem.priority;
      this.item.dealSourceContact = this.inputCrmItem.dealSourceContact;
      this.item.companyStage = this.inputCrmItem.companyStage;
      this.item.healthcareSubSector = this.inputCrmItem.healthcareSubSector;
      this.item.diseaseFocus = this.inputCrmItem.diseaseFocus;
      this.item.location = this.inputCrmItem.location;
      this.item.expectedClosingDate = this.inputCrmItem.expectedClosingDate;
      this.item.revenueModel = this.inputCrmItem.revenueModel;
      this.item.downsideSupport = this.inputCrmItem.downsideSupport;
      this.item.ltmRevenue = this.inputCrmItem.ltmRevenue;
      this.item.ltmEbitda = this.inputCrmItem.ltmEbitda;
      this.item.expectedIrr = this.inputCrmItem.expectedIrr;
      this.item.expectedMoic = this.inputCrmItem.expectedMoic;
      this.item.lastRoundValuation = this.inputCrmItem.lastRoundValuation;
      this.item.currentRoundValuation = this.inputCrmItem.currentRoundValuation;
      this.item.ndaSigned = this.inputCrmItem.ndaSigned;
      this.item.ndaExpiration = this.inputCrmItem.ndaExpiration;
      this.item.dealTypes = [...this.inputCrmItem.dealTypes];
      this.item.dealSourceType = this.inputCrmItem.dealSourceType;
      this.item.dealSourceTypeFreeText = this.inputCrmItem.dealSourceTypeFreeText;
      this.item.sourceUsers = [...this.inputCrmItem.sourceUsers];
      this.item.summary = this.inputCrmItem.summary;
      this.item.nextSteps = this.inputCrmItem.nextSteps;
      this.item.nonDeal = this.inputCrmItem.nonDeal;

      this.moreDetails = this.hasMoreDetails();
      if (this.inputCrmItem.expectedClosingDate) {
        this.closingDate = DateTimeService.parseDate(this.item.expectedClosingDate);
      }
      if (this.inputCrmItem.ndaExpiration) {
        this.ndaDate = DateTimeService.parseDate(this.inputCrmItem.ndaExpiration);
      }
    } else {
      this.item.id = null;
      this.item.name = null;
      this.item.status = StructCapCRMStatus.INITIAL_INBOUND;
      this.item.description = null;
      this.item.domains = new Array<Domain>();
      this.item.participants = new Array<{updated: Date, created: Date, user: User, confirmed: boolean, leader: boolean, triageOwner: boolean}>();
      this.silenceUntilDate = null;
      this.item.alarms = new Array<CRMAlarm>();
      this.item.externalIds = [];
      this.item.leader = {
        updated: new Date(),
        created: new Date(),
        user: this.authenticationService.getLoggedUser(),
        confirmed: true
      };
      this.item.triageOwner = null;

      this.item.companies = [{company: null, storm: null, etaki: null}];
      if (this.inputCompanyId) {
        this.companyService.getCompany(this.inputCompanyId, {extrafield: 'domains'}).subscribe((resp) => {
          const newCrmCompany: any = new CompanyBasic(resp);
          newCrmCompany['updated'] = new Date();
          newCrmCompany['created'] = new Date();
          newCrmCompany['createdBy'] = this.authenticationService.getLoggedUser();
          newCrmCompany['updatedBy'] = this.authenticationService.getLoggedUser();

          this.item.companies = [{company: newCrmCompany, storm: CRMCompanyStorm.NONE, etaki: ''}];
          if (resp.domains && resp.domains.length === 1 && this.item.domains && this.item.domains.length === 0) {
            this.item.domains = resp.domains;
          }
        });
      }

      this.item.dealTypes = new Array<StructCapDealType>();
      this.item.sourceUsers = new Array<User>();
    }
  }

  private getCrmData() {
    const leaderId = (this.item.leader && this.item.leader.id) ||
      (this.item.leader && this.item.leader.user && this.item.leader.user.id);
    const triageOwnerId =  (this.item.triageOwner && this.item.triageOwner.id) ||
      (this.item.triageOwner && this.item.triageOwner.user && this.item.triageOwner.user.id);
    return {
      id: this.item.id,
      name: this.item.name,
      status: this.item.status,
      description: this.item.description,
      domainIds: [...this.item.domains.map(g => g.id)],
      leaderId: leaderId || (leaderId === 0 ? 0 : null),
      triageOwnerId: triageOwnerId || (triageOwnerId === 0 ? 0 : null),
      indicated: this.item.indicated,
      received: this.item.received,
      externalIds: this.item.externalIds.filter(d => d.externalId && d.idType),
      ownerIds: [this.item.leader, ...this.item.participants].map(o => {
        if (o['id'] === 0) {
          return 0;
        }
        const co = o as any;
        return co.id || co.user.id;
      }),
      companies: [... this.item.companies.filter(c => c.company !== null).map(c => {
        const cc = c as any;
        let stormVal = cc.storm;
        if ((!stormVal || stormVal === CRMCompanyStorm.NONE) && !cc.company.isPrivate) {
          stormVal = CRMCompanyStorm.PUBLIC;
        }
        return {
          companyId: cc.id || cc.company.id,
          etaki: cc.etaki,
          storm: stormVal,
          courtshipType: cc.company.courtshipType
        };
      })],
      alarms: this.item.alarms || [],

      capIqId: this.item.capIqId,
      totalDealSize: this.item.totalDealSize,
      raDealSize: this.item.raDealSize,
      priority: this.item.priority,
      dealSourceContact: this.item.dealSourceContact,
      companyStage: this.item.companyStage,
      healthcareSubSector: this.item.healthcareSubSector,
      diseaseFocus: this.item.diseaseFocus,
      location: this.item.location,
      expectedClosingDate: this.closingDate,
      revenueModel: this.item.revenueModel,
      downsideSupport: this.item.downsideSupport,
      ltmRevenue: this.item.ltmRevenue,
      ltmEbitda: this.item.ltmEbitda,
      expectedIrr: this.item.expectedIrr,
      expectedMoic: this.item.expectedMoic,
      lastRoundValuation: this.item.lastRoundValuation,
      currentRoundValuation: this.item.currentRoundValuation,
      ndaSigned: this.item.ndaSigned,
      ndaExpiration: this.ndaDate,
      dealTypes: this.item.dealTypes,
      dealSourceType: this.item.dealSourceType,
      dealSourceTypeFreeText: this.item.dealSourceTypeFreeText,
      sourceIds: [...this.item.sourceUsers].map(o => {
        if (o['id'] === 0) {
          return 0;
        }
        const co = o as any;
        return co.id || co.user.id;
      }),
      silenceUntil: this.silenceUntilDate,
      summary: this.item.summary,
      nextSteps: this.item.nextSteps,
      nonDeal: this.item.nonDeal
    };
  }

  private companyAutocomplete() {
    this.companyTypeahead.pipe(
      distinctUntilChanged(),
      debounceTime(200),
      switchMap(term => {
        this.companySearching = true;
        return this.crmService.searchCompanies({ q: term });
      }),
    ).subscribe((value: any) => {
      this.companySearching = false;
      this.companyList = value;
      this.cd.markForCheck();
    }, () => {
      this.companySearching = false;
      this.companyList = new Array<CompanyBasic>();
    });
  }

  private hasMoreDetails(): boolean {
    return this.inputCrmItem && this.rmsUtils.isNotNullOrUndefined(this.item.capIqId) ||
      this.rmsUtils.isNotNullOrUndefined(this.item.companyStage) ||
      this.rmsUtils.isNotNullOrUndefined(this.item.healthcareSubSector) ||
      this.rmsUtils.isNotNullOrUndefined(this.item.diseaseFocus) ||
      this.rmsUtils.isNotNullOrUndefined(this.item.location) ||
      this.rmsUtils.isNotNullOrUndefined(this.item.expectedClosingDate) ||
      this.rmsUtils.isNotNullOrUndefined(this.item.revenueModel) ||
      this.rmsUtils.isNotNullOrUndefined(this.item.downsideSupport) ||
      this.rmsUtils.isNotNullOrUndefined(this.item.ltmRevenue) ||
      this.rmsUtils.isNotNullOrUndefined(this.item.ltmEbitda) ||
      this.rmsUtils.isNotNullOrUndefined(this.item.expectedIrr) ||
      this.rmsUtils.isNotNullOrUndefined(this.item.expectedMoic) ||
      this.rmsUtils.isNotNullOrUndefined(this.item.lastRoundValuation) ||
      this.rmsUtils.isNotNullOrUndefined(this.item.currentRoundValuation) ||
      this.rmsUtils.isNotNullOrUndefined(this.item.ndaSigned) ||
      this.rmsUtils.isNotNullOrUndefined(this.item.ndaExpiration);
  }

  private setSelectOptions() {
    this.healthcareSubsectorSelectOptions = this.getHealthcareSubSectorOptions();
    this.statusSelectOptions = this.getStatusOptions();
    this.dealTypeSelectOptions = this.getDealTypeOptions();
    this.dealSourceTypeSelectOptions = this.getDealSourceTypeOptions();
    this.companyStageSelectOptions = this.getCompanyStageOptions();
  }

  private getHealthcareSubSectorOptions() {
    return Object.keys(HealthcareSubSector).map(key => ({key, name: HealthcareSubSectorLabels[key]}));
  }

  private getDealTypeOptions() {
    return Object.keys(StructCapDealType);
  }

  private getDealSourceTypeOptions() {
    return Object.keys(StructCapCrmDealSourceType);
  }

  private getStatusOptions() {
    return Object.keys(StructCapCRMStatus)
      .filter(key => key !== StructCapCRMStatus.FOLLOW_UP_REACTIVATED)
      .map(key => ({key, name: StructCapCRMStatusLabels[key]}));
  }

  private getCompanyStageOptions() {
    return Object.keys(CompanyStage).map(key => ({key, name: CompanyStageLabels[key]}));
  }
}
